import { memo } from 'react'

import { Box, TableCell, TableRow, Typography, useTheme } from '@mui/material'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { describeWeek, WEEK_GROUP_HEADER_SIZE } from 'domains/Workspaces/Events/weekGrouping'

// Plain values rather than the run's rows: the rows array is rebuilt whenever the row model changes,
// which would leave the memo below with nothing to skip.
type WeekGroupHeaderProps = {
  startTime?: string | null
  eventCount: number
  weekendCount: number
  /** More of this week may still be on a page that has not loaded, so the counts are a lower bound. */
  isOpenEnded?: boolean
  /** The table head is sticky at the top, so the band parks underneath it rather than over it. */
  stickyOffset: number
}

const WeekGroupHeaderRaw = ({
  startTime,
  eventCount,
  weekendCount,
  isOpenEnded,
  stickyOffset,
}: WeekGroupHeaderProps) => {
  const { t } = useTranslation(['domain'])
  const theme = useTheme()

  const now = new Date()
  const week = describeWeek(startTime, now)

  if (!week) {
    return null
  }

  // Only the three weeks around now get a name. Further out the dates carry the header on their
  // own, and lead it.
  const label =
    week.offsetFromNow === 0
      ? t('domain:Workspace.Event.WeekGrouping.thisWeek')
      : week.offsetFromNow === 1
        ? t('domain:Workspace.Event.WeekGrouping.nextWeek')
        : week.offsetFromNow === -1
          ? t('domain:Workspace.Event.WeekGrouping.lastWeek')
          : undefined

  const eventCountLabel = isOpenEnded
    ? t('domain:Workspace.Event.WeekGrouping.eventCountAtLeast', { count: eventCount })
    : t('domain:Workspace.Event.WeekGrouping.eventCount', { count: eventCount })
  const weekendCountLabel = isOpenEnded
    ? t('domain:Workspace.Event.WeekGrouping.onTheWeekendAtLeast', { count: weekendCount })
    : t('domain:Workspace.Event.WeekGrouping.onTheWeekend', { count: weekendCount })
  const sameMonth = week.start.getMonth() === week.end.getMonth()
  // The year only shows up once the week leaves the current one; without it, the same week of two
  // different years would read identically.
  const yearSuffix = week.end.getFullYear() === now.getFullYear() ? '' : ' yyyy'
  const span = `${format(week.start, sameMonth ? 'd' : 'd MMM')} – ${format(week.end, `d MMM${yearSuffix}`)}`

  return (
    // A row, not a Box: the body is a <tbody>, and a bare div in there is hoisted out of the table.
    <TableRow
      sx={{
        display: 'block',
        position: 'sticky',
        top: `${stickyOffset}px`,
        zIndex: theme.zIndex.fab - 1,
      }}
    >
      <TableCell
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: 1.5,
          height: `${WEEK_GROUP_HEADER_SIZE}px`,
          boxSizing: 'border-box',
          px: 2,
          py: 0,
          backgroundColor: theme.palette.background.levels[700],
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {label ? (
          <>
            <Typography variant="heading-sm">{label}</Typography>
            <Typography variant="text-sm" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {span}
            </Typography>
          </>
        ) : (
          <Typography variant="heading-sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {span}
          </Typography>
        )}

        <Box aria-hidden sx={{ width: '1px', height: 14, backgroundColor: theme.palette.divider }} />

        <Typography variant="text-sm" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {weekendCount > 0 ? `${eventCountLabel} · ${weekendCountLabel}` : eventCountLabel}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

export const WeekGroupHeader = memo(WeekGroupHeaderRaw)
